/**
 * One shared AudioContext with two buses (WOW · feel):
 *   sfx bus   → gentle compressor → speakers   (volume = Settings SFX volume)
 *   music bus → speakers                        (volume × ducking while the teacher talks)
 * plus a self-generated "room" reverb both buses can send to. Nothing is loaded from files.
 */
import { useFeelPrefs } from './feelPrefs';
import { isHidden, onPowerChange } from './power';

export interface Buses {
  ctx: AudioContext;
  sfx: GainNode;
  music: GainNode;
  /** Reverb send (wet only). */
  room: GainNode;
}

let buses: Buses | null = null;

function impulse(ctx: AudioContext, seconds: number, decay: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return buf;
}

/** Shared buses (created on first use; resumes a suspended context). */
export function audioBuses(): Buses | null {
  if (typeof window === 'undefined') return null;
  const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!C) return null;
  if (!buses) {
    try {
      const ctx = new C();
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.ratio.value = 3;
      comp.attack.value = 0.004;
      comp.release.value = 0.2;
      comp.connect(ctx.destination);
      const sfx = ctx.createGain();
      sfx.connect(comp);
      const music = ctx.createGain();
      music.gain.value = 0;
      music.connect(ctx.destination);
      const conv = ctx.createConvolver();
      conv.buffer = impulse(ctx, 2.2, 2.6);
      const room = ctx.createGain();
      room.gain.value = 0.9;
      room.connect(conv).connect(comp);
      buses = { ctx, sfx, music, room };
      applySfxVolume(useFeelPrefs.getState().sfxVolume);
      useFeelPrefs.subscribe((s, prev) => s.sfxVolume !== prev.sfxVolume && applySfxVolume(s.sfxVolume));
    } catch {
      return null;
    }
  }
  if (buses.ctx.state === 'suspended' && !isHidden()) void buses.ctx.resume().catch(() => undefined);
  armIdleSuspend();
  return buses;
}

/* The WebAudio render thread runs (and keeps the audio hardware awake) while the context is "running", even silent:
   suspend it after 6 s without sounds when no music plays, and whenever the app is hidden. */
let busy: () => boolean = () => false;
export const setAudioBusyCheck = (f: () => boolean) => void (busy = f);
let idleTimer: ReturnType<typeof setTimeout> | null = null;
function armIdleSuspend() {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    idleTimer = null;
    if (buses && !busy() && buses.ctx.state === 'running') void buses.ctx.suspend().catch(() => undefined);
  }, 6000);
}
onPowerChange(() => {
  if (!buses) return;
  if (isHidden()) void buses.ctx.suspend().catch(() => undefined);
  else if (busy()) void buses.ctx.resume().catch(() => undefined);
});

/** Perceptual curve: slider 0..1 → gain. */
export const volumeGain = (v: number) => Math.pow(Math.max(0, Math.min(1, v)), 1.6);

function applySfxVolume(v: number) {
  if (!buses) return;
  buses.sfx.gain.setTargetAtTime(volumeGain(v) * 1.15, buses.ctx.currentTime, 0.05);
}
